import {
  $convertFromMarkdownString,
  MultilineElementTransformer,
  Transformer,
} from '@lexical/markdown';
import { $isElementNode, LexicalNode } from 'lexical';
import {
  $createSuggestionNode,
  $getSuggestionState,
  $isSuggestionNode,
} from '../../nodes/SuggestionNode';
import { Source } from '../../types';

const VALID_SOURCES: Source[] = ['default', 'note', 'inline', 'placeholder', 'deletion'];

export function isValidSource(source: string | null): source is Source {
  return VALID_SOURCES.includes(source as Source);
}

export function parseAttributes(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /(\w+)="([^"]*)"/g;
  let match;
  while ((match = re.exec(attrString)) !== null) {
    attrs[match[1]] = match[2];
  }
  return attrs;
}

export const createSuggestionTransformer = (
  transformers: Transformer[],
): MultilineElementTransformer => ({
  type: 'multiline-element',
  dependencies: [],

  regExpStart: /^<suggestion([^>]*)>$/,
  regExpEnd: /^<\/suggestion>$/,

  export: (node: LexicalNode, traverseChildren) => {
    if (!$isSuggestionNode(node)) {
      return null;
    }

    const state = $getSuggestionState(node);
    const attrs: string[] = [];
    if (state.source !== 'default') attrs.push(`source="${state.source}"`);
    if (state.tooltip) attrs.push(`tooltip="${state.tooltip}"`);
    const attrStr = attrs.length > 0 ? ` ${attrs.join(' ')}` : '';

    const output: string[] = [];

    output.push(`<suggestion${attrStr}>`);

    const elementTransformers = transformers.filter(
      (
        t,
      ): t is MultilineElementTransformer & {
        export: NonNullable<MultilineElementTransformer['export']>;
      } => (t.type === 'element' || t.type === 'multiline-element') && 'export' in t,
    );

    const childParts: string[] = [];
    for (const child of node.getChildren()) {
      let result: string | null = null;

      if ($isElementNode(child)) {
        for (const t of elementTransformers) {
          result = t.export(child, traverseChildren);
          if (result !== null) break;
        }
      }

      if (result !== null) {
        childParts.push(result);
      } else if ($isElementNode(child)) {
        childParts.push(traverseChildren(child));
      } else {
        childParts.push(child.getTextContent());
      }
    }

    output.push(childParts.join('\n'));
    output.push('</suggestion>');
    return output.join('\n');
  },

  replace: (parentNode, children, startMatch, endMatch, linesInBetween) => {
    if (!startMatch || !endMatch) {
      return;
    }

    const attrs = parseAttributes(startMatch[1] || '');
    const source: Source = isValidSource(attrs.source) ? attrs.source : 'default';
    const tooltip = attrs.tooltip || null;

    const suggestion = $createSuggestionNode(source, true, tooltip);
    if (children) {
      suggestion.append(...children);
    } else if (linesInBetween) {
      $convertFromMarkdownString(linesInBetween.join('\n'), transformers, suggestion);
    }

    parentNode.append(suggestion);

    suggestion.selectNext();
  },
});
